import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  setRuntimeContext,
  setSettings,
} from '../../../../support/slices/tileBuilderSlice';
import { RootState, useAppDispatch } from '../../../../support/store';
import { TileRuntimeContext } from '../../../../support/tileRuntimeContext';
import { initialSettings, TileSettings } from '../../../../types/tileBuilderTypes';
import ChartColorsControls from '../ChartControls/ChartColorsControls';
import BarLabelsTotalsControls from '../ChartControls/BarLabelsTotalsControls';
import PieDonutGaugeSpecificControls from '../ChartControls/PieDonutGaugeSpecificControls';
import BarChartSpecificControls from '../ChartControls/BarChartSpecificControls';
import { CHART_DATE_RANGE_NAMES } from '../ChartControls/ChartControlProps';
import ChartTooltipControls from '../ChartControls/ChartTooltipControls';
import LineChartSpecificControls from '../ChartControls/LineChartSpecificControls';
import {
  getEffectiveMargins,
  getLayoutStrategy,
} from './tilePreviewHelpers';
import { ChartKind, DataSource } from './tilePreviewTypes';
import TilePreviewColumnsModal from './TilePreviewColumnsModal';
import DisplayContextControls from '../ChartControls/DisplayContextControls';
import RuntimeContextControls from '../ChartControls/RuntimeContextControls';
import DataFieldsControls from '../ChartControls/DataFieldsControls';
import LayoutLegendControls from '../ChartControls/LayoutLegendControls';
import AxesGridControls from '../ChartControls/AxesGridControls';

interface TilePreviewControlsProps {
  chartKind: ChartKind;
  isSmall: boolean;
  chartId: string;
  allSources?: DataSource[];
  dateFilterEnabled?: boolean;
  handleOpenColumnsModal?: () => void;
  previewData?: any;
  derivedFields?: string[];
}

function TilePreviewControls({
                               chartKind,
                               chartId,
                               isSmall,
                               allSources = [],
                               dateFilterEnabled,
                               handleOpenColumnsModal,
                               previewData,
                               derivedFields = [],
                             }: TilePreviewControlsProps) {
  const dispatch = useAppDispatch();

  const {
    displayContext = 'preview',
    runtimeContext: previewRuntimeContext = {},
    selectedSourceIdx = 0,
    leaderLayout,
  } = useSelector((state: RootState) => state.tileBuilder?.[chartId]?.preview) || {};

  const settings: TileSettings = useSelector(
    (state: RootState) => state.tileBuilder?.[chartId]?.tile?.settings || initialSettings
  );

  const isBarChart = chartKind === 'bar';
  const isLineChart = chartKind === 'line';
  const isPieLike = chartKind === 'pie' || chartKind === 'donut' || chartKind === 'gauge';
  const isDatagrid = chartKind === 'datagrid';
  const isMetric = chartKind === 'metric';

  const showDataFields = true;
  const showLayoutLegend = !isDatagrid && !isMetric && chartKind !== 'unknown';
  const showTooltip = !isDatagrid && !isMetric && chartKind !== 'unknown';
  const showAxesGrid = isBarChart || isLineChart;
  const showBar = isBarChart;
  const showLine = isLineChart;
  const showPieLike = isPieLike;

  const effectiveMargins = getEffectiveMargins(settings, displayContext);
  const legendShown = !!settings.show_legend;
  const layoutStrategy = getLayoutStrategy(settings);

  const [isColumnsModalOpen, setIsColumnsModalOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | false>('dataFields');
  const [controlsOpen, setControlsOpen] = useState(false);

  const togglePanel =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) =>
      setExpanded(isExpanded ? panel : false);

  const setContext = (update: Partial<TileRuntimeContext>) => {
    dispatch(
      setRuntimeContext({
        chartId,
        context: { ...previewRuntimeContext, ...update },
      })
    );
  };

  const allowedDateRangeOptions = useMemo(() => {
    const raw = settings.dateRangeOptions ?? [CHART_DATE_RANGE_NAMES[0].value];
    return raw.length > 0 ? raw : [CHART_DATE_RANGE_NAMES[0].value];
  }, [settings.dateRangeOptions]);

  const visibleDateRangeOptions = useMemo(() => {
    return CHART_DATE_RANGE_NAMES.filter((option) =>
      allowedDateRangeOptions.includes(option.value)
    );
  }, [allowedDateRangeOptions]);

  const effectiveSelectedDateRange = useMemo(() => {
    const runtimeValue = previewRuntimeContext.current_time_filter;
    const defaultValue = settings.defaultDateRange;
    const fallback = allowedDateRangeOptions[0] ?? CHART_DATE_RANGE_NAMES[0].value;

    const preferred = runtimeValue ?? defaultValue ?? fallback;
    return allowedDateRangeOptions.includes(preferred) ? preferred : fallback;
  }, [
    previewRuntimeContext.current_time_filter,
    settings.defaultDateRange,
    allowedDateRangeOptions,
  ]);

  const syncDateRange = (nextValue: string) => {
    const safeValue = allowedDateRangeOptions.includes(nextValue)
      ? nextValue
      : allowedDateRangeOptions[0] ?? CHART_DATE_RANGE_NAMES[0].value;

    dispatch(
      setSettings({
        chartId,
        settings: {
          defaultDateRange: safeValue,
        },
      })
    );

    setContext({
      current_time_filter: safeValue,
    });
  };

  const controls = (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Accordion
          expanded={expanded === 'displayContext'}
          onChange={togglePanel('displayContext')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Display Context</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DisplayContextControls
              chartId={chartId}
              displayContext={displayContext}
              settings={settings as any}
              leaderLayout={leaderLayout}
              dispatch={dispatch}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expanded === 'runtimeContext'}
          onChange={togglePanel('runtimeContext')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Runtime Context (Testing)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <RuntimeContextControls
              previewRuntimeContext={previewRuntimeContext}
              setContext={setContext}
            />
          </AccordionDetails>
        </Accordion>

        {showDataFields && (
          <Accordion expanded={expanded === 'dataFields'} onChange={togglePanel('dataFields')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Data & Fields</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <DataFieldsControls
                chartId={chartId}
                settings={settings}
                dispatch={dispatch}
                selectedSourceIdx={selectedSourceIdx}
                allSources={allSources}
                isDatagrid={isDatagrid}
                isMetric={isMetric}
                isBarChart={isBarChart}
                isLineChart={isLineChart}
                derivedFields={derivedFields}
                previewData={previewData}
                dateFilterEnabled={dateFilterEnabled}
                allowedDateRangeOptions={allowedDateRangeOptions}
                visibleDateRangeOptions={visibleDateRangeOptions}
                effectiveSelectedDateRange={effectiveSelectedDateRange}
                currentTimeFilter={previewRuntimeContext.current_time_filter}
                handleOpenColumnsModal={() => {
                  setIsColumnsModalOpen(true);
                  handleOpenColumnsModal?.();
                }}
                syncDateRange={syncDateRange}
              />
            </AccordionDetails>
          </Accordion>
        )}

        {!isDatagrid && !isMetric && chartKind !== 'unknown' && (
          <Accordion expanded={expanded === 'colors'} onChange={togglePanel('colors')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Colors</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ChartColorsControls
                chartId={chartId}
                settings={settings}
                dispatch={dispatch}
                chartKind={chartKind}
                isLineChart={isLineChart}
              />
            </AccordionDetails>
          </Accordion>
        )}

        {showLayoutLegend && (
          <Accordion
            expanded={expanded === 'layoutLegend'}
            onChange={togglePanel('layoutLegend')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Layout & Legend</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <LayoutLegendControls
                chartId={chartId}
                settings={settings}
                dispatch={dispatch}
                displayContext={displayContext}
                legendShown={legendShown}
                layoutStrategy={layoutStrategy}
                effectiveMargins={effectiveMargins}
                leaderLayout={leaderLayout}
              />
            </AccordionDetails>
          </Accordion>
        )}

        {showAxesGrid && (
          <Accordion expanded={expanded === 'axes'} onChange={togglePanel('axes')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Axes & Grid</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AxesGridControls
                chartId={chartId}
                settings={settings}
                dispatch={dispatch}
                chartKind={chartKind}
                isLineChart={isLineChart}
              />
            </AccordionDetails>
          </Accordion>
        )}

        {showTooltip && (
          <Accordion expanded={expanded === 'tooltip'} onChange={togglePanel('tooltip')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Tooltip</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ChartTooltipControls
                chartId={chartId}
                settings={settings}
                dispatch={dispatch}
                chartKind={chartKind}
                isLineChart={isLineChart}
              />
            </AccordionDetails>
          </Accordion>
        )}

        {showBar && (
          <Accordion expanded={expanded === 'bar'} onChange={togglePanel('bar')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Bar Chart</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <BarChartSpecificControls
                chartId={chartId}
                settings={settings}
                dispatch={dispatch}
                chartKind={chartKind}
              />
              <Divider sx={{ my: 2 }} />
              <BarLabelsTotalsControls
                chartId={chartId}
                settings={settings}
                dispatch={dispatch}
                chartKind={chartKind}
                isLineChart={false}
              />
            </AccordionDetails>
          </Accordion>
        )}

        {showLine && (
          <Accordion expanded={expanded === 'line'} onChange={togglePanel('line')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Line Chart</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <LineChartSpecificControls
                chartId={chartId}
                settings={settings}
                dispatch={dispatch}
                chartKind={chartKind}
              />
            </AccordionDetails>
          </Accordion>
        )}

        {showPieLike && (
          <Accordion expanded={expanded === 'pieLike'} onChange={togglePanel('pieLike')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Pie / Donut / Gauge</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <PieDonutGaugeSpecificControls
                chartId={chartId}
                settings={settings}
                dispatch={dispatch}
                chartKind={chartKind}
              />
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </Box>
  );

  const modal = isDatagrid ? (
    <TilePreviewColumnsModal
      chartId={chartId}
      derivedFields={derivedFields}
      open={isColumnsModalOpen}
      onClose={() => setIsColumnsModalOpen(false)}
    />
  ) : null;

  if (!isSmall) {
    return (
      <>
        <Box
          sx={{
            width: 560,
            minWidth: 560,
            maxWidth: 700,
            height: '100%',
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
          }}
        >
          {controls}
        </Box>
        {modal}
      </>
    );
  }

  return (
    <>
      <Box sx={{ position: 'fixed', right: 16, bottom: 16, zIndex: 1300 }}>
        <IconButton
          onClick={() => setControlsOpen(true)}
          color="primary"
          sx={{ bgcolor: 'background.paper', boxShadow: 3 }}
          aria-label="Open chart controls"
        >
          <TuneIcon />
        </IconButton>
      </Box>

      <Drawer anchor="left" open={controlsOpen} onClose={() => setControlsOpen(false)}>
        <Box sx={{ width: 420, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              p: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle1">Chart Controls</Typography>
            <IconButton onClick={() => setControlsOpen(false)} aria-label="Close controls">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ flex: 1, overflow: 'auto' }}>{controls}</Box>
        </Box>
      </Drawer>
      {modal}
    </>
  );
}

export default TilePreviewControls;